import { useMemo } from "react"
import { ELEMENT_ICONS } from "@/constants/guardians"
import {
  ElementCardAssets,
  resolveCardElementAssets,
  resolvePowerIcon,
  resolveStyleIcon,
} from "@/constants/cardAssets"
import { calcBaseBS } from "@/constants/game.constants"
import {
  BattleCardBreakdown,
  BattleCardView,
  GameCard,
} from "@/types/battle"
import CardFooterMeta, { FooterMetaMode } from "./CardFooterMeta"
import CardInfoStrip from "./CardInfoStrip"
import CardRankSurface from "./CardRankSurface"
import CardShapeLayer from "./CardShapeLayer"
import CardShardBadges from "./CardShardBadges"
import { computeCardShapeLayout } from "./cardShapeLayout"
import { buildCardShapePaths } from "./cardShapePaths"

export interface GuardianCardFaceProps {
  card: BattleCardView | GameCard
  width: number
  height: number
  combatPower: number
  assets?: ElementCardAssets
  showBreakdown?: BattleCardBreakdown
  footerMeta?: FooterMetaMode
  hideFooterMeta?: boolean
  heroReplayKey?: string | number
  shardScale?: number
}

export function getCardCombatPower(card: BattleCardView | GameCard): number {
  if ("baseBS" in card && card.baseBS) return card.baseBS
  return calcBaseBS(card)
}

export default function GuardianCardFace({
  card,
  width,
  height,
  assets,
  combatPower,
  footerMeta = "full",
  hideFooterMeta = false,
  heroReplayKey,
  shardScale = 1,
}: GuardianCardFaceProps) {
  const resolvedFooterMeta: FooterMetaMode = hideFooterMeta
    ? "hidden"
    : footerMeta
  const elementAssets = useMemo(
    () => resolveCardElementAssets(card, assets),
    [card, assets]
  )

  const layout = useMemo(
    () => computeCardShapeLayout(width, height),
    [width, height]
  )
  const paths = useMemo(() => buildCardShapePaths(layout), [layout])

  return (
    <div
      className="overflow-hidden"
      style={{ width, height, borderRadius: layout.cardRadius }}
    >
      <CardRankSurface
        colorRank={card.colorRank}
        style={{
          width,
          height,
          borderRadius: layout.cardRadius,
          padding: layout.gutter,
          overflow: "hidden",
          boxShadow: "0 4px 14px rgba(0,0,0,0.35)",
        }}
      >
        <div
          className="relative"
          style={{ width: layout.innerW, height: layout.innerH }}
        >
          <CardShapeLayer
            width={layout.innerW}
            height={layout.bottomBlockH}
            top={layout.bottomLayerTop}
            path={paths.footer}
            imageSource={elementAssets.footerBackground}
            imageAlign="center"
          />

          {resolvedFooterMeta !== "hidden" && (
            <CardFooterMeta
              card={card}
              layout={layout}
              mode={resolvedFooterMeta}
            />
          )}

          <CardShardBadges
            card={card}
            layout={layout}
            sizeScale={shardScale}
          />

          <CardInfoStrip
            layout={layout}
            combatPower={combatPower}
            elementIcon={elementAssets.elementIcon}
            styleIcon={resolveStyleIcon(card.style, elementAssets)}
            powerIcon={resolvePowerIcon(elementAssets)}
          />

          <CardShapeLayer
            width={layout.innerW}
            height={layout.topBlockH}
            top={0}
            path={paths.hero}
            imageSource={elementAssets.heroImage}
            imageAlign="top"
            imageReplayKey={heroReplayKey}
            className="z-[3]"
          >
            {!elementAssets.heroImage && (
              <div className="flex h-full w-full items-center justify-center bg-[#27252B]">
                <span style={{ fontSize: 40 * layout.scale }}>
                  {ELEMENT_ICONS[card.element] ?? "🃏"}
                </span>
              </div>
            )}
          </CardShapeLayer>
        </div>
      </CardRankSurface>
    </div>
  )
}
